import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { CreateModulePasswordlessAccessModel } from 'app/library/module-passwordless-access/models/create-module-passwordless-access';
import { PasswordlessModuleSettings } from 'app/library/module-passwordless-access/models/passwordless-module-settings.model';
import { ModulePasswordlessAccessService } from 'app/library/module-passwordless-access/services/services/module-passwordless-access.service';
import { Module } from 'app/library/module/models/module.model';
import { ModuleApiService } from 'app/library/module/services/module.api-service';
import { CreatePasswordlessAccessModel } from 'app/library/passwordless-access/models/create-passwordless-access';
import { PasswordlessAccess } from 'app/library/passwordless-access/models/passwordless-access.model';
import { PasswordlessAccessService } from 'app/library/passwordless-access/services/services/passwordless-access.service';
import { HelperService } from 'app/projects/shared/src/lib/services/helper';
import * as moment from 'moment';

@Component({
    selector: 'app-passwordless-access-dialog',
    templateUrl: './passwordless-access-dialog.component.html',
    styleUrls: ['./passwordless-access-dialog.component.scss'],
})
export class PasswordlessAccessDialogComponent implements OnInit {
    @Input()
    selectedUserIds: string[] = [];

    @Input()
    selectedAccesses: PasswordlessAccess[] = [];

    @Input()
    currentModule: Module;

    passwordlessAccess: PasswordlessAccess;
    isEditDialog: boolean = false;
    minDate: Date;
    maxDate: Date;
    isSaveInProgress: boolean = false;
    today = new Date();
    maximumPossibleDate: Date;
    modulePasswordlessSettings: PasswordlessModuleSettings;

    private _formGroup: FormGroup;

    get formGroup(): FormGroup {
        return this._formGroup;
    }

    constructor(
        public dialogRef: MatDialogRef<PasswordlessAccessDialogComponent>,
        private _formBuilder: FormBuilder,
        private _helperService: HelperService,
        private _passwordlessAccessService: PasswordlessAccessService,
        private _moduleApiService: ModuleApiService,
        private _modulePasswordlessAccessService: ModulePasswordlessAccessService
    ) {}

    async ngOnInit(): Promise<void> {
        this.passwordlessAccess = this.selectedAccesses.length === 1 ? this.selectedAccesses[0] : null;
        this.isEditDialog = !!this.selectedAccesses.length;

        this._formGroup = this._formBuilder.group({
            startDate: [{ value: this.passwordlessAccess ? moment(this.passwordlessAccess.start) : null, disabled: this.isEditDialog }, [Validators.required]],
            startTime: [{ value: this.passwordlessAccess ? moment(this.passwordlessAccess.start).format('HH:mm') : null, disabled: this.isEditDialog }, Validators.required],
            endDate: [this.passwordlessAccess ? moment(this.passwordlessAccess.end) : null, Validators.required],
            endTime: [this.passwordlessAccess ? moment(this.passwordlessAccess.end).format('HH:mm') : null || null, Validators.required],
            generateAccessCode: [{ value: false, disabled: this.passwordlessAccess }],
        });

        if (this.isEditDialog) {
            this.updateEndDate();
        }

        if (this.currentModule) {
            this.modulePasswordlessSettings = await this._moduleApiService.getPasswordlessLoginSettings(this.currentModule.id);
            this.maximumPossibleDate = new Date(this.modulePasswordlessSettings.latestEnd);
        }
    }

    updateStartDate(): void {
        const endDate = this.formGroup.get('endDate').value;

        this.maxDate = endDate ? new Date(endDate) : null;
    }

    updateEndDate(): void {
        const startDate = this.formGroup.get('startDate').value;

        this.minDate = startDate ? new Date(startDate) : null;
    }

    async save(): Promise<void> {
        this.isSaveInProgress = true;
        const data = this.formGroup.getRawValue();

        const startTime = this._helperService.cleaveTimeFixer(data.startTime);
        const endTime = this._helperService.cleaveTimeFixer(data.endTime);

        if (data.startDate?.format('YYYY-MM-DD') === data.endDate?.format('YYYY-MM-DD') && startTime >= endTime) {
            this._helperService.showSnackBarMessage('FORM.EVENT.MESSAGE.END_DATE_MUST_BE_GT_START_DATE');
            this.isSaveInProgress = false;

            return;
        }

        if (!this.isEditDialog) {
            const payload: CreatePasswordlessAccessModel = {
                start: new Date(`${data.startDate.format('YYYY-MM-DD'.replace(/-/g, '/'))} ${startTime}`).toISOString(),
                end: new Date(`${data.endDate.format('YYYY-MM-DD'.replace(/-/g, '/'))} ${endTime}`).toISOString(),
                generateAccessCode: data.generateAccessCode,
                userIds: this.selectedUserIds,
            };

            const successMessage = 'PASSWORDLESS_ACCESS.MESSAGE.ACCESS_CREATED';

            if (this.currentModule) {
                const data: CreateModulePasswordlessAccessModel = { ...payload, moduleId: this.currentModule.id };
                const success = await this._modulePasswordlessAccessService.createModulePasswordlessAccess(data);

                if (success) {
                    this._helperService.showSnackBarMessage(successMessage);
                    this.dialogRef.close();
                    this._modulePasswordlessAccessService.fetchModulePasswordlessAccesses(this.currentModule.id, 10, 1, '');
                }
            } else {
                const success = await this._passwordlessAccessService.createPasswordlessAccess(payload);

                if (success) {
                    this._helperService.showSnackBarMessage(successMessage);
                    this.dialogRef.close();
                    this._passwordlessAccessService.fetchPasswordlessAccesses(10, 1, '');
                }
            }
        } else {
            let success: boolean;

            if (this.currentModule) {
                success = await this._modulePasswordlessAccessService.updateModulePasswordlessAccess({
                    end: new Date(`${data.endDate.format('YYYY-MM-DD'.replace(/-/g, '/'))} ${endTime}`).toISOString(),
                    accessIds: this.selectedAccesses.map((access) => access.id),
                });
            } else {
                success = await this._passwordlessAccessService.updatePasswordlessAccess({
                    end: new Date(`${data.endDate.format('YYYY-MM-DD'.replace(/-/g, '/'))} ${endTime}`).toISOString(),
                    accessIds: this.selectedAccesses.map((access) => access.id),
                });
            }

            if (success) {
                this._helperService.showSnackBarMessage('PASSWORDLESS_ACCESS.MESSAGE.ACCESS_EDITED');
                this.dialogRef.close();
            }
        }

        this.isSaveInProgress = false;
    }
}
